<script setup>
import { computed, ref, watch, onScopeDispose } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useCrmAgents } from 'dashboard/composables/useCrmAgents';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import { useKanbanRefresh } from 'dashboard/composables/useKanbanRefresh';
import tasksApi from 'dashboard/api/kanban/tasks';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  deals: { type: Array, default: () => [] },
  showDealLink: { type: Boolean, default: false },
});
const emit = defineEmits(['openDeal', 'changed']);
const { t } = useI18n();
const { accountId } = useAccount();
const {
  agentsList,
  hasError: agentsError,
  isPending: agentsLoading,
  load: reloadAgents,
} = useCrmAgents();
const { run, isPending } = useAbortableRequest();
const tasks = ref([]);
const view = ref('pending');
const page = ref(0);
const hasMore = ref(false);
const selectedDealId = ref(null);
const error = ref('');
const editing = ref(null);
const title = ref('');
const description = ref('');
const assigneeId = ref(null);
const dueAt = ref('');
const saving = ref(false);
const savedDraft = ref('');
const draftSnapshot = () =>
  JSON.stringify({
    title: title.value.trim(),
    description: description.value,
    assignee: assigneeId.value,
    dueAt: dueAt.value,
  });
const hasUnsavedChanges = computed(() =>
  Boolean(
    saving.value || (editing.value && draftSnapshot() !== savedDraft.value)
  )
);
let requestId;
let disposed = false;
const now = ref(Date.now());
const timer = setInterval(() => {
  now.value = Date.now();
}, 60000);
onScopeDispose(() => {
  disposed = true;
  clearInterval(timer);
});
const context = computed(() => `${accountId.value}:${selectedDealId.value}`);
const filters = [
  'pending',
  'mine',
  'today',
  'overdue',
  'unassigned',
  'completed',
];

const load = async (background = false) => {
  error.value = '';
  if (background !== true) {
    tasks.value = [];
    hasMore.value = false;
  }
  try {
    await run(async signal => {
      const { data } = await tasksApi.get(
        {
          deal_id: selectedDealId.value || undefined,
          view: view.value,
          page: page.value,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        { signal }
      );
      if (signal.aborted) return;
      tasks.value = data.tasks;
      hasMore.value = data.has_more;
    });
  } catch {
    error.value = t('KANBAN.TASKS.LOAD_ERROR');
  }
};

useKanbanRefresh(
  () => load(true),
  () => !saving.value && !isPending.value
);

const edit = task => {
  if (hasUnsavedChanges.value) return;
  editing.value = task || {};
  requestId = crypto.randomUUID();
  title.value = task?.title || '';
  description.value = task?.description || '';
  assigneeId.value = task?.assignee?.id || null;
  if (task?.due_at) {
    const date = new Date(task.due_at);
    dueAt.value = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
  } else {
    dueAt.value = '';
  }
  savedDraft.value = draftSnapshot();
};

const save = async () => {
  if (saving.value || !title.value.trim()) return;
  const source = context.value;
  const draft = editing.value;
  saving.value = true;
  error.value = '';
  try {
    const attributes = {
      title: title.value.trim(),
      description: description.value,
      assignee_id: assigneeId.value || null,
      due_at: dueAt.value ? new Date(dueAt.value).toISOString() : null,
    };
    let response;
    if (draft.id) {
      response = await tasksApi.update(draft.id, {
        task: { ...attributes, lock_version: draft.lock_version },
      });
    } else {
      response = await tasksApi.create({
        task: {
          ...attributes,
          deal_id: selectedDealId.value,
          request_id: requestId,
        },
      });
    }
    if (disposed || context.value !== source || editing.value !== draft) return;
    editing.value = null;
    emit('changed', response.data.deal);
    await load();
  } catch (failure) {
    if (!disposed && context.value === source) {
      error.value = t(
        failure.response?.status === 409
          ? 'KANBAN.TASKS.CONFLICT'
          : 'KANBAN.TASKS.SAVE_ERROR'
      );
    }
  } finally {
    saving.value = false;
  }
};

const toggle = async (task, event) => {
  event.target.checked = !!task.completed_at;
  if (saving.value) return;
  const source = context.value;
  saving.value = true;
  error.value = '';
  try {
    const { data } = await tasksApi.update(task.id, {
      task: { completed: !task.completed_at, lock_version: task.lock_version },
    });
    if (!disposed && context.value === source) {
      emit('changed', data.deal);
      await load();
    }
  } catch (failure) {
    if (!disposed && context.value === source) {
      error.value = t(
        failure.response?.status === 409
          ? 'KANBAN.TASKS.CONFLICT'
          : 'KANBAN.TASKS.SAVE_ERROR'
      );
    }
  } finally {
    saving.value = false;
  }
};

const selectDeal = id => {
  if (id === selectedDealId.value) return true;
  if (hasUnsavedChanges.value) return false;
  selectedDealId.value = id;
  return true;
};

watch(
  () => props.deals,
  deals => {
    if (!deals.some(deal => deal.id === selectedDealId.value))
      selectedDealId.value = deals[0]?.id || null;
  },
  { immediate: true }
);
watch([context, view], () => {
  editing.value = null;
  page.value = 0;
});
watch([context, view, page], load, { immediate: true });
defineExpose({ refresh: load, hasUnsavedChanges, saving, selectDeal });
</script>

<template>
  <section
    class="flex min-w-0 flex-col gap-3"
    :class="{ 'max-w-5xl mx-auto': showDealLink }"
    :aria-label="t('KANBAN.TASKS.TITLE')"
  >
    <button
      v-if="agentsError"
      type="button"
      class="text-xs text-n-ruby-11"
      data-test-id="retry-agents"
      @click="reloadAgents"
    >
      {{ t('KANBAN.ERRORS.FETCH_ASSIGNEES') }}
    </button>
    <div class="flex flex-wrap items-center justify-between gap-2">
      <h2
        class="m-0 font-semibold text-n-slate-12"
        :class="showDealLink ? 'text-2xl' : 'text-base'"
      >
        {{ t('KANBAN.TASKS.TITLE') }}
      </h2>
      <div class="flex items-center gap-1">
        <Button
          :disabled="isPending || saving"
          icon="i-lucide-refresh-cw"
          variant="ghost"
          size="sm"
          :title="t('KANBAN.TASKS.REFRESH')"
          :aria-label="t('KANBAN.TASKS.REFRESH')"
          @click="load"
        />
        <Button
          v-if="selectedDealId"
          :disabled="hasUnsavedChanges"
          icon="i-lucide-plus"
          size="sm"
          :label="t('KANBAN.TASKS.ADD')"
          @click="edit(null)"
        />
      </div>
    </div>
    <p v-if="showDealLink" class="m-0 text-sm text-n-slate-11">
      {{ t('KANBAN.TASKS.QUEUE_DESCRIPTION') }}
    </p>
    <div class="flex flex-wrap gap-2">
      <div
        v-if="showDealLink"
        class="flex flex-wrap gap-1.5 py-2"
        :aria-label="t('KANBAN.TASKS.FILTER')"
      >
        <button
          v-for="filter in filters"
          :key="filter"
          type="button"
          :aria-pressed="view === filter"
          :disabled="hasUnsavedChanges"
          :data-test-id="`task-filter-${filter}`"
          class="min-h-10 rounded-lg px-3 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-n-brand"
          :class="
            view === filter
              ? 'bg-n-brand/10 text-n-brand'
              : 'text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="view = filter"
        >
          {{ t(`KANBAN.TASKS.VIEWS.${filter}`) }}
        </button>
      </div>
      <select
        v-if="deals.length > 1"
        v-model.number="selectedDealId"
        :disabled="hasUnsavedChanges"
        :aria-label="t('KANBAN.TITLE')"
        class="min-w-0 flex-1 rounded border border-n-weak bg-n-background px-2 py-1 text-sm"
      >
        <option v-for="deal in deals" :key="deal.id" :value="deal.id">
          {{
            deal.title ||
            deal.product_name ||
            deal.kanban_funnel?.name ||
            `#${deal.id}`
          }}
        </option>
      </select>
      <select
        v-if="!showDealLink"
        v-model="view"
        :disabled="hasUnsavedChanges"
        data-test-id="task-view"
        :aria-label="t('KANBAN.TASKS.FILTER')"
        class="min-w-0 flex-1 rounded border border-n-weak bg-n-background px-2 py-1 text-sm"
      >
        <option v-for="filter in filters" :key="filter" :value="filter">
          {{ t(`KANBAN.TASKS.VIEWS.${filter}`) }}
        </option>
      </select>
    </div>
    <p v-if="error" role="alert" class="m-0 text-sm text-n-ruby-11">
      {{ error }}
    </p>
    <form
      v-if="editing"
      class="flex min-w-0 flex-col gap-2 border-y border-n-weak py-3"
      @submit.prevent="save"
    >
      <label class="flex flex-col gap-1 text-xs"
        >{{ t('KANBAN.TASKS.SUBJECT') }}
        <input
          v-model="title"
          data-test-id="task-title"
          required
          maxlength="240"
          class="min-w-0 rounded border border-n-weak bg-n-background px-2 py-1 text-sm"
        />
      </label>
      <label class="flex flex-col gap-1 text-xs"
        >{{ t('KANBAN.NOTES_LABEL') }}
        <textarea
          v-model="description"
          maxlength="5000"
          rows="2"
          class="min-w-0 rounded border border-n-weak bg-n-background px-2 py-1 text-sm"
        />
      </label>
      <label class="flex flex-col gap-1 text-xs"
        >{{ t('CONVERSATION_SIDEBAR.ASSIGNEE_LABEL') }}
        <select
          v-model.number="assigneeId"
          data-test-id="task-assignee"
          :disabled="agentsLoading || agentsError"
          class="min-w-0 rounded border border-n-weak bg-n-background px-2 py-1 text-sm"
        >
          <option :value="null">{{ t('KANBAN.SIDEBAR.NO_ASSIGNEE') }}</option>
          <option v-for="agent in agentsList" :key="agent.id" :value="agent.id">
            {{ agent.name }}
          </option>
        </select>
      </label>
      <label class="flex flex-col gap-1 text-xs"
        >{{ t('KANBAN.TASKS.DUE') }}
        <input
          v-model="dueAt"
          data-test-id="task-due"
          type="datetime-local"
          class="w-full min-w-0 rounded border border-n-weak bg-n-background px-2 py-1 text-sm"
        />
      </label>
      <div class="flex gap-2">
        <Button
          type="submit"
          data-test-id="task-save"
          :disabled="saving || !title.trim()"
          :is-loading="saving"
          :label="t('KANBAN.SAVE')"
          size="sm"
        />
        <Button
          :disabled="saving"
          :label="t('KANBAN.CANCEL')"
          variant="ghost"
          size="sm"
          @click="editing = null"
        />
      </div>
    </form>
    <Spinner v-if="isPending && !tasks.length" :size="18" />
    <ul
      v-else
      class="m-0 list-none divide-y divide-n-weak p-0"
      :class="{
        'rounded-xl border border-n-weak bg-n-solid-2 px-4': showDealLink,
      }"
    >
      <li
        v-for="task in tasks"
        :key="task.id"
        class="flex min-w-0 items-start gap-3 py-4"
      >
        <input
          type="checkbox"
          :checked="!!task.completed_at"
          :disabled="saving"
          :aria-label="
            t(
              task.completed_at
                ? 'KANBAN.TASKS.REOPEN'
                : 'KANBAN.TASKS.COMPLETE',
              { title: task.title }
            )
          "
          class="mt-1 shrink-0 size-4 cursor-pointer"
          @change="toggle(task, $event)"
        />
        <div class="min-w-0 flex-1">
          <p
            class="m-0 break-words text-sm font-medium text-n-slate-12"
            :class="{ 'line-through': task.completed_at }"
          >
            {{ task.title }}
          </p>
          <p
            v-if="task.deal?.title"
            class="m-0 break-words text-xs text-n-slate-11"
          >
            {{ task.deal.title }}
          </p>
          <p class="m-0 break-words text-xs text-n-slate-10">
            {{ task.deal?.contact?.name }} &middot;
            {{ task.assignee?.name || t('KANBAN.SIDEBAR.NO_ASSIGNEE') }}
          </p>
          <Button
            v-if="showDealLink && task.deal"
            :disabled="hasUnsavedChanges"
            variant="link"
            size="sm"
            :label="t('KANBAN.TASKS.OPEN_DEAL')"
            @click="emit('openDeal', task.deal)"
          />
          <p
            v-if="task.due_at"
            class="m-0 text-xs"
            :class="
              !task.completed_at && new Date(task.due_at).getTime() < now
                ? 'text-n-ruby-11'
                : 'text-n-slate-11'
            "
          >
            {{ new Date(task.due_at).toLocaleString() }}
            <span
              v-if="!task.completed_at && new Date(task.due_at).getTime() < now"
            >
              &middot; {{ t('KANBAN.TASKS.VIEWS.overdue') }}</span
            >
          </p>
          <p v-if="task.completed_at" class="m-0 text-xs text-n-teal-11">
            {{
              t('KANBAN.TASKS.COMPLETED_BY', {
                name: task.completed_by?.name || '',
                date: new Date(task.completed_at).toLocaleString(),
              })
            }}
          </p>
        </div>
        <Button
          :disabled="hasUnsavedChanges"
          icon="i-lucide-pencil"
          variant="ghost"
          size="sm"
          :title="t('KANBAN.SIDEBAR.EDIT_DEAL')"
          :aria-label="t('KANBAN.TASKS.EDIT', { title: task.title })"
          @click="edit(task)"
        />
      </li>
      <li
        v-if="!tasks.length && !error"
        class="py-8 text-sm text-n-slate-11"
        :class="{ 'text-center': showDealLink }"
      >
        {{ t('KANBAN.TASKS.EMPTY') }}
      </li>
    </ul>
    <div v-if="page || hasMore" class="flex items-center justify-end gap-2">
      <Button
        :disabled="!page || isPending"
        icon="i-lucide-chevron-left"
        variant="ghost"
        :aria-label="t('KANBAN.TASKS.PREVIOUS')"
        :title="t('KANBAN.TASKS.PREVIOUS')"
        @click="page -= 1"
      />
      <span class="text-xs">{{ page + 1 }}</span>
      <Button
        :disabled="!hasMore || isPending"
        icon="i-lucide-chevron-right"
        variant="ghost"
        :aria-label="t('KANBAN.TASKS.NEXT')"
        :title="t('KANBAN.TASKS.NEXT')"
        @click="page += 1"
      />
    </div>
  </section>
</template>
